import { Alert, Spin, Tabs } from 'antd';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { GroupItemStore, GroupType } from 'src/stores/groups-store';
import { AppConfig, container } from 'src/inversify.config';

import GroupGeneralPropertyEditCard from './components/group-general-properties-card';
import GroupMembersTab from './components/group-members-tab';
import GroupRolesTab from './components/group-roles-tab';

import 'src/assets/cards-detail-view-layout.less';
import { OrganizationItemStore } from 'src/stores/organizations-store';
import { ENV } from 'src/';
import { AppConfiguration } from 'src/core/services/configuration.service';
import GroupLicenseRolesTab from './components/group-license-roles-tab';

const GroupDetailPage: FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  let groupId = params.id!;
  const groupStore = container.get(GroupItemStore);
  const groupState = groupStore.state;

  const organizationStore = container.get(OrganizationItemStore);
  const organizationState = organizationStore.state;
  const [isPartner, setIsPartner] = useState<boolean>(false);
  const config: AppConfiguration = useContext(AppConfig);

  useEffect(() => {
    load();
  }, [groupId, groupStore]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    await groupStore.load(groupId);
    await organizationStore.load(groupState.item.organizationId.value);
    setIsPartner(organizationState.item.isPartner.value);

    const mainContainer = document.querySelector('main.ant-layout-content');
    if (mainContainer) {
      mainContainer.classList.add('edit-mode');
    }
    return () => {
      mainContainer && mainContainer.classList.remove('edit-mode');
    };
  };

  if (groupState.item.value) {
    setHeader(t('GroupParam').replace('{0}', groupState.item.name.value));
    setRoutes([
      { path: '', breadcrumbName: t('Home') },
      { path: 'groups', breadcrumbName: t('Groups') },
      { path: groupState.item.id.value, breadcrumbName: groupState.item.name.value },
    ]);
  }

  return (
    <div className="entity-details_card-grid-container">
      <Spin spinning={groupState.isBusy.get() || organizationState.isBusy.get()} size="large">
        {groupState.errorMessage.value && (
          <Alert message={config.environment === ENV.DEV ? groupState.errorMessage.value : t('UnexpectedError')} type="error" />
        )}
        {!groupState.errorMessage.value && (
          <div className="entity-details_card-grid-container__form-container">
            <div className="entity-details_card-grid-container__left-container" style={{ width: '30%', minWidth: 200, borderRadius: 8 }}>
              <GroupGeneralPropertyEditCard groupId={groupId} />
            </div>
            <div className="entity-details_card-grid-container__right-container" style={{ width: '70%', minWidth: 200, borderRadius: 8 }}>
              <Tabs defaultActiveKey="1" type="card">
                <Tabs.TabPane tab={t('Members')} key="1">
                  <div className="tab-container">
                    <GroupMembersTab />
                  </div>
                </Tabs.TabPane>
                {groupState.item?.value?.groupType !== GroupType.License && (
                  <Tabs.TabPane tab={t('Roles')} key="3">
                    <div className="tab-container">
                      <GroupRolesTab
                        groupName={groupState.item?.value?.name ?? ''}
                        organizationName={groupState.item?.value?.organization ?? ''}
                        groupId={groupId}
                        isPartnerOrg={isPartner}
                        groupOrganizationId={groupState.item?.value?.organizationId || undefined}
                      />
                    </div>
                  </Tabs.TabPane>
                )}

                {groupState.item?.value?.groupType === GroupType.License && (
                  <Tabs.TabPane tab={t('Roles')} key="2">
                    <div className="tab-container">
                      <GroupLicenseRolesTab
                        groupName={groupState.item?.value?.name ?? ''}
                        organizationName={groupState.item?.value?.organization ?? ''}
                        groupId={groupId}
                        groupOrganizationId={groupState.item?.value?.organizationId || undefined}
                        isPartnerOrg={isPartner}
                      />
                    </div>
                  </Tabs.TabPane>
                )}
              </Tabs>
            </div>
          </div>
        )}
      </Spin>
    </div>
  );
};

export default GroupDetailPage;
